import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useUserContext } from '../context/UserContext';

const TimelineContext = createContext();

export const useTimelineContext = () => useContext(TimelineContext);

const cleanText = (value, fallback = '') => {
  if (typeof value !== 'string') return fallback;
  return value.trim() || fallback;
};

const cleanTags = (tags) => {
  if (!Array.isArray(tags)) return [];
  return Array.from(new Set(
    tags
      .filter(tag => typeof tag === 'string')
      .map(tag => tag.trim())
      .filter(Boolean)
  ));
};

export const TimelineProvider = ({ children }) => {
  const { userTimelines, updateTimeline } = useUserContext();
  const [events, setEvents] = useState([]);
  const [currentTimeline, setCurrentTimeline] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [activeTagFilter, setActiveTagFilter] = useState(null);
  const [allTags, setAllTags] = useState([]);
  const [updateQueued, setUpdateQueued] = useState(false);

  const ensureDate = useCallback((dateInput) => {
    if (dateInput instanceof Date) return dateInput;
    if (typeof dateInput === 'string') return new Date(dateInput);
    if (typeof dateInput === 'number') return new Date(dateInput);
    return new Date(NaN);
  }, []);

  const normalizeStoredEvent = useCallback((event) => {
    const date = ensureDate(event?.date);
    if (!Number.isFinite(date.getTime())) return null;

    const hasEndDate = event?.endDate !== undefined && event?.endDate !== null && event?.endDate !== '';
    const hasValidEndDate = hasEndDate && Number.isFinite(ensureDate(event.endDate).getTime());
    const endDate = hasValidEndDate ? ensureDate(event.endDate) : null;
    if (event?.type === 'range' && (!endDate || endDate.getTime() < date.getTime())) return null;

    const type = event?.type === 'range' ? 'range' : 'point';
    const position = event?.position === 'bottom' ? 'bottom' : 'top';

    return {
      ...event,
      id: event?.id ?? `${date.getTime()}-${cleanText(event?.title, 'untitled')}`,
      title: cleanText(event?.title, 'Untitled Event'),
      description: cleanText(event?.description),
      tags: cleanTags(event?.tags),
      position: type === 'range' ? 'bottom' : position,
      date: date.getTime(),
      endDate: type === 'range' ? endDate.getTime() : null,
      type
    };
  }, [ensureDate]);

  const setCurrentTimelineById = useCallback((timelineId) => {
    const timeline = userTimelines.find(t => t.id === Number(timelineId));
    if (!timeline) {
      setCurrentTimeline(null);
      setEvents([]);
      setAllTags([]);
      setEditingEvent(null);
      setActiveTagFilter(null);
      setUpdateQueued(false);
      return null;
    }

    setCurrentTimeline(timeline);
    const normalizedEvents = (timeline.events || [])
      .map(normalizeStoredEvent)
      .filter(Boolean);
    setEvents(normalizedEvents);
    setAllTags(cleanTags(timeline.tags));
    return timeline;
  }, [normalizeStoredEvent, userTimelines]);

  // Batch timeline updates
  useEffect(() => {
    if (updateQueued && currentTimeline?.id) {
      const updatedTimeline = {
        ...currentTimeline,
        events,
        tags: allTags
      };
      updateTimeline(currentTimeline.id, updatedTimeline);
      setUpdateQueued(false);
    }
  }, [updateQueued, events, allTags, currentTimeline, updateTimeline]);

  const queueUpdate = useCallback(() => {
    setUpdateQueued(true);
  }, []);

  const addEvent = useCallback((event) => {
    const normalizedEvent = {
      ...event,
      date: ensureDate(event.date).getTime(),
      endDate: event.endDate ? ensureDate(event.endDate).getTime() : null,
      position: event.position || 'top',
      type: event.endDate ? 'range' : 'point'
    };

    setEvents(prev => [...prev, normalizedEvent].sort((a, b) => a.date - b.date));
    if (Array.isArray(event.tags)) {
      setAllTags(prev => Array.from(new Set([...prev, ...cleanTags(event.tags)])));
    }
    queueUpdate();
  }, [ensureDate, queueUpdate]);

  const updateEvent = useCallback((updatedEvent) => {
    const normalizedEvent = {
      ...updatedEvent,
      date: ensureDate(updatedEvent.date).getTime(),
      endDate: updatedEvent.endDate ? ensureDate(updatedEvent.endDate).getTime() : null,
      type: updatedEvent.endDate ? 'range' : 'point',
      position: updatedEvent.position || 'top'
    };

    setEvents(prev => 
      prev.map(event => 
        event.id === updatedEvent.id ? normalizedEvent : event
      ).sort((a, b) => a.date - b.date)
    );
    
    if (Array.isArray(updatedEvent.tags)) {
      setAllTags(prev => Array.from(new Set([...prev, ...cleanTags(updatedEvent.tags)])));
    }
    setEditingEvent(null);
    queueUpdate();
  }, [ensureDate, queueUpdate]);

  const deleteEvent = useCallback((eventId) => {
    setEvents(prev => prev.filter(event => event.id !== eventId));
    setEditingEvent(null);
    queueUpdate();
  }, [queueUpdate]);

  const renameCurrentTimeline = useCallback((name) => {
    const trimmedName = name.trim();
    if (!currentTimeline?.id || !trimmedName) return false;

    setCurrentTimeline(prev => prev ? { ...prev, name: trimmedName } : prev);
    updateTimeline(currentTimeline.id, { name: trimmedName });
    return true;
  }, [currentTimeline, updateTimeline]);

  // Add function to clean up unused tags
  const cleanupUnusedTags = useCallback(() => {
    const usedTags = new Set();
    events.forEach(event => {
      if (Array.isArray(event.tags)) {
        event.tags.forEach(tag => usedTags.add(tag));
      }
    });
    setAllTags(Array.from(usedTags));
  }, [events]);

  // Simplify getFilteredEvents back to tag filtering
  const filteredEvents = useMemo(() => {
    if (!activeTagFilter) return events;
    return events.filter(event => 
      Array.isArray(event.tags) && event.tags.includes(activeTagFilter)
    );
  }, [events, activeTagFilter]);

  return (
    <TimelineContext.Provider value={{ 
      events,
      filteredEvents,
      currentTimeline,
      setCurrentTimelineById,
      addEvent, 
      updateEvent, 
      deleteEvent,
      renameCurrentTimeline,
      editingEvent,
      setEditingEvent,
      allTags,
      activeTagFilter,
      setActiveTagFilter,
      cleanupUnusedTags
    }}>
      {children}
    </TimelineContext.Provider>
  );
};
